export interface BugDecisionSnapshot {
  id: string;
  source?: 'chat' | 'mail' | 'files';
  status: string;
  fix_approved?: boolean;
  fix_approved_at?: string | null;
}
interface Reply {ok: boolean; status: number; data: Record<string, unknown>}
type Action = 'approve' | 'retriage';
interface Decision {
  before: BugDecisionSnapshot;
  action: Action;
  approved: boolean;
  send: () => Promise<Reply>;
  read: () => Promise<BugDecisionSnapshot | null>;
}
export interface BugDecisionOutcome {
  kind: 'confirmed' | 'rejected' | 'uncertain';
  message: string;
  retryAllowed: boolean;
}

export async function sendBugDecision(id: string, action: Action, approved: boolean, fetcher: typeof fetch = fetch): Promise<Reply> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetcher(`/api/super-admin/bug-reports/${encodeURIComponent(id)}/${action}`, {
      method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(action === 'approve' ? {approved} : {}), signal: controller.signal,
    });
    const value: unknown = await response.json().catch(() => null);
    return {ok: response.ok, status: response.status, data: value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}};
  } finally { clearTimeout(timer); }
}

function errorCode(data: Record<string, unknown>): unknown {
  // Interpret only known codes below; never display upstream text or private details.
  let detail: unknown = data.detail;
  if (typeof detail === 'string') { try { detail = JSON.parse(detail); } catch { detail = null; } }
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const value = detail as Record<string, unknown>;
    if (value.error && typeof value.error === 'object') return (value.error as Record<string, unknown>).code;
    return value.error_code ?? value.error;
  }
  return data.error;
}

function savedMessage(decision: Decision, after: BugDecisionSnapshot | null): string {
  if (decision.action === 'retriage') return 'Обращение возвращено на разбор.';
  if (!decision.approved) return 'Одобрение отозвано.';
  if (after?.fix_approved === false && after.fix_approved_at) {
    if (after.status === 'needs_human' || after.status === 'enhancement') return 'Одобрение сохранено, но попытка агента вернулась на разбор. Повторная попытка требует нового одобрения.';
    if (after.status === 'in_progress') return 'Одобрение сохранено. Агент уже взял обращение в работу.';
  }
  return 'Одобрение сохранено.';
}

export function bugDecisionReadConfirms(before: BugDecisionSnapshot, action: Action, approved: boolean, current: BugDecisionSnapshot | null): boolean {
  const after = current?.id === before.id && (!current.source || current.source === 'chat') ? current : null;
  const date = after?.fix_approved_at;
  const newApproval = typeof date === 'string' && Number.isFinite(Date.parse(date)) && date !== before.fix_approved_at;
  return after !== null && (action === 'retriage'
    ? before.status === 'rejected' && after.status === 'new'
    : approved ? after.fix_approved === true || newApproval
      : before.fix_approved === true && after.fix_approved === false && after.fix_approved_at === null);
}

/** One mutation, then one read. Lost acknowledgements never cause automatic resubmission. */
export async function runBugDecision(decision: Decision): Promise<BugDecisionOutcome> {
  let response: Reply | null = null;
  try { response = await decision.send(); } catch { /* Reconcile the same report, not the mutation. */ }
  let after: BugDecisionSnapshot | null = null;
  try {
    const current = await decision.read();
    if (current?.id === decision.before.id && (!current.source || current.source === 'chat')) after = current;
  } catch { /* Keep the unconfirmed decision visible. */ }
  const readConfirmed = bugDecisionReadConfirms(decision.before, decision.action, decision.approved, after);
  if ((response?.ok && response.data.ok === true) || readConfirmed) {
    return {kind: 'confirmed', message: savedMessage(decision, after) + (after ? '' : ' Список обновить не удалось. Проверьте статус обращения.'), retryAllowed: false};
  }
  const code = response ? errorCode(response.data) : null;
  if (response?.status === 401) return {kind: 'rejected', message: 'Сессия супер-администратора истекла. Войдите снова.', retryAllowed: true};
  if (code === 'SECURITY_FLAGGED') return {kind: 'rejected', message: 'Обращение помечено как подозрительное. Нужен ручной разбор.', retryAllowed: true};
  if (code === 'NOT_APPROVABLE' || code === 'ALREADY_STARTED' || code === 'NOT_REJECTED') return {kind: 'rejected', message: 'Статус обращения уже изменился. Проверьте текущий статус в списке.', retryAllowed: true};
  if (response && response.status >= 400 && response.status < 500 && response.status !== 408) return {kind: 'rejected', message: 'Действие не принято. Проверьте доступ и текущий статус обращения.', retryAllowed: true};
  return {kind: 'uncertain', message: 'Не удалось подтвердить результат. Не отправляйте одобрение повторно до проверки статуса обращения.', retryAllowed: false};
}
